import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { photoboothGalleryApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, X, ChevronLeft, ChevronRight, Loader2, Archive, Image as ImageIcon, Lock } from "lucide-react";
import { useSeo } from "@/lib/seo";

/**
 * Public photobooth gallery — accessible via a share code from an email.
 * Route: /galerie/:code
 */
export default function PublicGallery() {
  const { code: routeCode } = useParams();
  const [code, setCode] = useState((routeCode || "").toUpperCase());
  const [manualCode, setManualCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!routeCode);
  const [error, setError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [zipDownloading, setZipDownloading] = useState(false);

  useSeo({
    title: data ? `${data.display_name || "Galerie"} · My Animation` : "Galerie photobooth · My Animation",
    description: "Consultez et téléchargez vos photos du photobooth My Animation.",
    noindex: true, // never index a private gallery
  });

  useEffect(() => {
    if (!code) {
      setLoading(false);
      return;
    }
    let cancel = false;
    setLoading(true);
    setError("");
    photoboothGalleryApi.publicGet(code)
      .then((d) => { if (!cancel) setData(d); })
      .catch((e) => {
        if (cancel) return;
        setError(formatApiError(e) || "Code invalide.");
        setData(null);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [code]);

  const photos = useMemo(() => data?.photos || [], [data]);

  const openAt = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(-1);
  const prev = () => setLightboxIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
  const next = () => setLightboxIndex((i) => (i < photos.length - 1 ? i + 1 : 0));

  useEffect(() => {
    if (lightboxIndex < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxIndex, photos.length]);

  const downloadOne = (p) => {
    const url = photoboothGalleryApi.publicFileUrl(code, p.id);
    const a = document.createElement("a");
    a.href = url;
    a.download = p.original_filename || `photo-${p.id}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadZip = async () => {
    setZipDownloading(true);
    try {
      const url = photoboothGalleryApi.publicZipUrl(code);
      // Trigger a browser download without CORS issues
      window.location.href = url;
      setTimeout(() => setZipDownloading(false), 3000);
    } catch (e) {
      toast.error("Impossible de télécharger l'archive.");
      setZipDownloading(false);
    }
  };

  // No code yet → ask for one
  if (!code) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-md card-luxe p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#DCB457]/15 flex items-center justify-center mx-auto mb-4">
            <Lock size={22} strokeWidth={1.4} className="text-[#DCB457]" />
          </div>
          <p className="eyebrow mb-2">Accès privé</p>
          <h1 className="font-serif-luxe text-3xl mb-3">Votre galerie <span className="gold-text italic">photobooth.</span></h1>
          <p className="text-white/60 text-sm mb-6">
            Saisissez le code d'accès que vous avez reçu par email pour consulter et télécharger vos photos.
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); if (manualCode.trim()) setCode(manualCode.trim().toUpperCase()); }}
            className="space-y-3"
          >
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              placeholder="Ex : X4KP2Z9M"
              autoFocus
              data-testid="gallery-code-input"
              maxLength={16}
              className="w-full bg-black/70 border border-[#DCB457]/25 focus:border-[#DCB457] px-4 py-3 text-center font-mono tracking-[0.4em] text-lg outline-none"
            />
            <button
              type="submit"
              data-testid="gallery-code-submit"
              disabled={!manualCode.trim()}
              className="btn-gold w-full py-3 disabled:opacity-50"
            >
              Accéder à mes photos
            </button>
          </form>
          <p className="text-white/40 text-xs mt-6 italic">Vous n'avez pas de code ? Contactez-nous au 07 69 22 49 56.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-[#DCB457]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center py-16 px-4">
        <div className="w-full max-w-md card-luxe p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <X size={22} strokeWidth={1.4} className="text-red-300" />
          </div>
          <h1 className="font-serif-luxe text-2xl mb-2">Code invalide.</h1>
          <p className="text-white/60 text-sm mb-6">{error || "Ce code ne correspond à aucune galerie."}</p>
          <button
            type="button"
            onClick={() => { setCode(""); setManualCode(""); setError(""); }}
            className="btn-gold px-5 py-2 text-sm"
            data-testid="gallery-retry-btn"
          >
            Saisir un autre code
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] py-12 md:py-16 px-4 md:px-8" data-testid="public-gallery">
      {/* Header */}
      <header className="max-w-7xl mx-auto mb-10">
        <p className="eyebrow mb-2">Vos photos · My Animation</p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-serif-luxe text-4xl md:text-5xl">
              {data.event_label ? (
                <>{data.event_label} <span className="gold-text italic">.</span></>
              ) : (
                <>Votre <span className="gold-text italic">galerie</span>.</>
              )}
            </h1>
            <p className="text-white/55 text-sm mt-2">
              {data.photos_count} photo{data.photos_count > 1 ? "s" : ""} disponible{data.photos_count > 1 ? "s" : ""}
              {" · "}Code <code className="font-mono text-[#DCB457]">{code}</code>
            </p>
          </div>
          {photos.length > 0 && (
            <button
              type="button"
              onClick={downloadZip}
              disabled={zipDownloading}
              data-testid="gallery-download-zip"
              className="btn-gold inline-flex items-center gap-2 self-start md:self-auto disabled:opacity-60"
            >
              {zipDownloading ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} strokeWidth={1.5} />}
              Tout télécharger (ZIP)
            </button>
          )}
        </div>
      </header>

      {/* Grid */}
      <div className="max-w-7xl mx-auto">
        {photos.length === 0 ? (
          <div className="card-luxe p-12 text-center">
            <ImageIcon size={30} strokeWidth={1} className="text-[#DCB457] mx-auto mb-3" />
            <p className="text-white/55 text-sm">Aucune photo n'est encore disponible dans votre galerie.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3" data-testid="gallery-grid">
            {photos.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => openAt(i)}
                data-testid={`gallery-thumb-${p.id}`}
                className="group relative aspect-square overflow-hidden border border-[#DCB457]/15 bg-black/40 hover:border-[#DCB457] transition-all"
              >
                <img
                  src={photoboothGalleryApi.publicFileUrl(code, p.id)}
                  alt={p.original_filename || `Photo ${i + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex >= 0 && photos[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center animate-fadeIn"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            data-testid="gallery-lightbox-close"
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            aria-label="Fermer"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
          {photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                data-testid="gallery-lightbox-prev"
                aria-label="Précédente"
              >
                <ChevronLeft size={20} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                data-testid="gallery-lightbox-next"
                aria-label="Suivante"
              >
                <ChevronRight size={20} strokeWidth={1.5} />
              </button>
            </>
          )}
          <img
            src={photoboothGalleryApi.publicFileUrl(code, photos[lightboxIndex].id)}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[85vh] object-contain"
          />
          <div
            className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-black/70 backdrop-blur px-4 py-2 border border-[#DCB457]/25"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-white/70 text-xs">{lightboxIndex + 1} / {photos.length}</span>
            <button
              type="button"
              onClick={() => downloadOne(photos[lightboxIndex])}
              data-testid="gallery-lightbox-download"
              className="inline-flex items-center gap-1.5 text-sm text-[#DCB457] hover:text-white transition-colors"
            >
              <Download size={13} strokeWidth={1.5} /> Télécharger
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
